import { format } from 'date-fns'

import { EVENT } from 'routes/paths'
import { ArtworkNode, VenueNode, ArtistNode } from 'gql'
import { time, getImageUrl } from '~utils'

// https://developers.google.com/search/docs/data-types/event
// If the event starts at a specific time, such as 5pm locally, use 2019-07-20T17:00:00
const dateToSchemaFormat = (date: Date) => format(date, 'yyyy-MM-dd hh:mm:ss')

type EventMeta = Pick<ArtworkNode, 'slug' | 'title' | 'description' | 'eventStartDateLocal'> & {
  venue?: Pick<VenueNode, 'name' | 'zip' | 'state' | 'city' | 'streetAddress' | 'timeZone'>
  mainArtist?: Pick<ArtistNode, 'fullName'>
  imageSrc: {
    path: string
    extension: string
  }
  vip?: boolean
}

export const getEventStartDate = ({
  eventStartDateLocal,
}: Pick<ArtworkNode, 'eventStartDateLocal'>) => time.formatDate(eventStartDateLocal, 'long')

export const getEventSubTitle = ({
  eventStartDateLocal,
  venue,
}: {
  eventStartDateLocal?: Pick<ArtworkNode, 'eventStartDateLocal'>
  venue?: Pick<VenueNode, 'name'>
}) => `${getEventStartDate({ eventStartDateLocal })} • ${venue.name}`

// https://schema.org/Event
// TODO: https://trello.com/c/sNjSiNEa/614-reconsider-auctions-types-schema
export const getEventSchema = ({
  title,
  venue,
  imageSrc,
  mainArtist,
  description,
  eventStartDateLocal,
  slug,
}: EventMeta) => {
  // https://schema.org/Place
  const location = venue
    ? {
        '@type': 'Place',
        address: {
          '@type': 'PostalAddress', // https://schema.org/PostalAddress
          postalCode: venue.zip,
          addressRegion: venue.state,
          addressLocality: venue.city,
          streetAddress: venue.streetAddress,
        },
        name: venue.name,
      }
    : {
        '@type': 'VirtualLocation',
        url: `https://granted.co${EVENT(slug)}`,
      }

  // https://schema.org/Person
  const performer = mainArtist
    ? {
        '@type': 'Person',
        name: mainArtist.fullName,
      }
    : undefined

  return {
    '@context': 'http://schema.org',
    '@type': 'Event',
    location,
    performer,
    name: title,
    description,
    eventStatus: 'EventScheduled',
    eventAttendanceMode: 'OfflineEventAttendanceMode',
    image: getImageUrl(imageSrc, 1200, 630),
    startDate: dateToSchemaFormat(new Date(eventStartDateLocal)),
  }
}

export const getEventMetaData = ({
  vip,
  title,
  venue,
  imageSrc,
  mainArtist,
  description,
  eventStartDateLocal,
}: EventMeta) => {
  const seoTitleArr = []

  if (mainArtist?.fullName) {
    seoTitleArr.push(mainArtist?.fullName)
  }

  seoTitleArr.push(title)

  if (eventStartDateLocal && venue) {
    seoTitleArr.push(
      getEventSubTitle({
        eventStartDateLocal,
        venue,
      })
    )
  }

  if (vip) {
    seoTitleArr.push('Vip experiences')
  }

  return {
    imageSrc,
    description,
    title: seoTitleArr.join(' • '),
  }
}

export const getTourSchema = (events: Array<EventMeta>) =>
  events.map((event) => getEventSchema(event))
